import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Clock, LogIn, LogOut, CalendarRange, AlertTriangle, Smartphone, X } from "lucide-react";
import { fmtWeekdayTime } from "@/lib/datetime";

const A2HS_DISMISS_KEY = "albushra_a2hs_dismissed";

function AddToHomeScreenHint() {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(A2HS_DISMISS_KEY) === "1");
  if (dismissed) return null;
  const dismiss = () => {
    localStorage.setItem(A2HS_DISMISS_KEY, "1");
    setDismissed(true);
  };
  return (
    <div
      className="mt-6 flex items-start gap-3 rounded-lg border border-primary/25 bg-primary/5 px-4 py-3 text-sm"
      data-testid="a2hs-banner"
    >
      <Smartphone className="h-4 w-4 mt-0.5 text-primary shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-foreground">
          Tip: Add this page to your home screen for one-tap clock-in.
        </p>
        <p className="mt-1 text-muted-foreground">
          <span className="font-medium text-foreground">iOS:</span> Share → Add to Home Screen ·{" "}
          <span className="font-medium text-foreground">Android:</span> Menu (⋮) → Add to Home Screen
        </p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        data-testid="a2hs-dismiss"
        aria-label="Dismiss tip"
        className="text-muted-foreground hover:text-foreground transition-colors shrink-0 -mr-1"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const nowHM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

// Minutes since midnight (America/Edmonton) for a UTC ISO timestamp.
function mtMinutes(iso) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Edmonton",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const hh = Number(parts.find((p) => p.type === "hour").value) % 24;
  const mm = Number(parts.find((p) => p.type === "minute").value);
  return hh * 60 + mm;
}

export default function ClockInOut() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // new-entry form
  const [date, setDate] = useState(toISO(new Date()));
  const [timeIn, setTimeIn] = useState(nowHM());

  // sign-out form
  const [signingOut, setSigningOut] = useState(false);
  const [timeOut, setTimeOut] = useState(nowHM());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/teacher/time/status");
      setStatus(data);
    } catch (e) {
      toast.error("Failed to load clock status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveEntry = async () => {
    if (!date || !timeIn) {
      toast.error("Pick a date and sign-in time");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/teacher/time/clock-in", { date, time_in: timeIn });
      setStatus(data);
      toast.success("Entry saved — sign out when you're done");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      load();
    } finally {
      setSaving(false);
    }
  };

  const saveSignOut = async () => {
    if (!timeOut) {
      toast.error("Pick a sign-out time");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/teacher/time/clock-out", { time_out: timeOut });
      setStatus(data);
      setSigningOut(false);
      toast.success("Signed out");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center mt-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const open = status?.open_entry;

  // Soft sanity check: would-be hours if signing out at the chosen time.
  let signOutHours = null;
  if (open && timeOut) {
    const [h, m] = timeOut.split(":").map(Number);
    if (!Number.isNaN(h)) {
      signOutHours = (h * 60 + m - mtMinutes(open.clock_in)) / 60;
    }
  }
  const overTen = signOutHours != null && signOutHours > 10;

  return (
    <div className="max-w-xl" data-testid="clock-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-foreground">
        Clock In / Out
      </h1>
      <p className="mt-1 text-muted-foreground">Log your working hours by date and time.</p>

      <AddToHomeScreenHint />

      {!open && status?.recent_auto_closed && (
        <div
          className="mt-6 rounded-lg border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/10 px-4 py-3 text-sm text-foreground"
          data-testid="auto-closed-notice"
        >
          Your previous session was automatically closed after 8 hours. If that's not
          right, ask an admin to adjust it on the Time Entries page.
        </div>
      )}

      {open ? (
        /* ---- Open entry: needs a sign-out ---- */
        <div className="mt-6 bg-card border border-border rounded-lg p-8" data-testid="open-entry-card">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-[hsl(var(--success))]/15 flex items-center justify-center">
              <Clock className="h-6 w-6 text-[hsl(var(--success))]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground" data-testid="clock-state">
                Open entry — needs sign-out
              </p>
              <p className="text-foreground font-medium" data-testid="open-entry-signin">
                Signed in: {fmtWeekdayTime(open.clock_in)}
              </p>
            </div>
          </div>

          {signingOut ? (
            <div className="mt-6 space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="time-out">Sign-out time</Label>
                <Input
                  id="time-out"
                  type="time"
                  className="h-11"
                  data-testid="time-entry-time-out"
                  value={timeOut}
                  onChange={(e) => setTimeOut(e.target.value)}
                />
              </div>
              {overTen && (
                <div
                  className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-foreground"
                  data-testid="over-hours-warning"
                >
                  <AlertTriangle className="h-4 w-4 mt-0.5 text-destructive shrink-0" />
                  <span>
                    This entry is {signOutHours.toFixed(1)} hours — please double check your
                    sign-in and sign-out times. You can still save if that's correct.
                  </span>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={saveSignOut} disabled={saving} className="h-11" data-testid="sign-out-save">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save sign-out"}
                </Button>
                <Button variant="ghost" className="h-11" onClick={() => setSigningOut(false)} disabled={saving}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              onClick={() => {
                setTimeOut(nowHM());
                setSigningOut(true);
              }}
              className="mt-6 h-12 bg-destructive hover:bg-destructive/90"
              data-testid="sign-out-button"
            >
              <LogOut className="h-5 w-5 mr-2" /> Sign Out
            </Button>
          )}
        </div>
      ) : (
        /* ---- No open entry: add a new one ---- */
        <div className="mt-6 bg-card border border-border rounded-lg p-8" data-testid="new-entry-card">
          <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground" data-testid="clock-state">
            Add a time entry
          </p>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="entry-date">Date</Label>
              <Input
                id="entry-date"
                type="date"
                className="h-11"
                data-testid="time-entry-date"
                value={date}
                max={toISO(new Date())}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="entry-time-in">Sign-in time</Label>
              <Input
                id="entry-time-in"
                type="time"
                className="h-11"
                data-testid="time-entry-time-in"
                value={timeIn}
                onChange={(e) => setTimeIn(e.target.value)}
              />
            </div>
          </div>
          <Button onClick={saveEntry} disabled={saving} className="mt-5 h-12" data-testid="time-entry-save">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><LogIn className="h-5 w-5 mr-2" /> Save entry</>}
          </Button>
        </div>
      )}

      {/* Week summary */}
      <div className="mt-4 bg-card border border-border rounded-lg p-6" data-testid="week-summary">
        <div className="flex items-center gap-2 text-muted-foreground">
          <CalendarRange className="h-4 w-4" />
          <span className="text-xs uppercase tracking-wide font-semibold">This week (Mon–Sun)</span>
        </div>
        <p className="mt-2 font-heading text-3xl font-medium text-foreground" data-testid="week-hours">
          {status?.week_hours ?? 0} <span className="text-lg text-muted-foreground">hours</span>
        </p>
        <p className="text-sm text-muted-foreground">
          {status?.week_start} → {status?.week_end}
        </p>
      </div>
    </div>
  );
}
